import type { ReactNode } from 'react';
import { Navigate, Outlet, ScrollRestoration, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RefreshCw, X } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { loginPath } from '../lib/navigation';
import { useSession } from '../stores/session';
import { PageSpinner } from './common';
import { Header } from './Header';
import { TimerTray } from './TimerTray';
import { Toaster } from './Toaster';

function UpdatePrompt() {
  const { t } = useTranslation();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      // Check for a new version every hour while the app stays open.
      if (registration) window.setInterval(() => void registration.update(), 60 * 60 * 1000);
    },
  });
  if (!needRefresh) return null;
  return (
    <div role="status" className="animate-pop-in fixed end-3 bottom-3 z-50 flex max-w-sm items-center gap-3 rounded-2xl border border-line bg-surface p-3 shadow-xl">
      <RefreshCw className="size-5 shrink-0 text-brand" />
      <p className="text-sm">{t('pwa.updateAvailable')}</p>
      <button type="button" className="btn-primary px-3 py-1.5" onClick={() => void updateServiceWorker(true)}>
        {t('pwa.update')}
      </button>
      <button type="button" className="icon-btn size-8" onClick={() => setNeedRefresh(false)} aria-label={t('common.close')}>
        <X className="size-4" />
      </button>
    </div>
  );
}

export function RootLayout() {
  return (
    <>
      <Header />
      <main id="main">
        <Outlet />
      </main>
      <Toaster />
      <TimerTray />
      <UpdatePrompt />
      <ScrollRestoration />
    </>
  );
}

/** Sends visitors who aren't signed in to the login page, then back here. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, ready } = useSession();
  const location = useLocation();
  if (!ready) return <PageSpinner />;
  if (!user) return <Navigate to={loginPath(location)} replace />;
  return children;
}
